import { TranscriptTrack } from '@src/editor/models/TranscriptTrack';
import { joinSpeakerTurns } from '@src/services/transcript/speakerTurns';

import type { ReelSection } from './deriveReelSections';
import type { ReelSectionName } from './reelTypes';
import type { VideoTranscriptElement } from '@src/editor/models/elements/VideoTranscriptElement';
import type { Timeline } from '@src/editor/models/Timeline';

/**
 * PLAT-11059 — the string a naming prompt reads.
 *
 * The whole input reaches the model as one unannounced string: the endpoint puts
 * `inputs.baseText.text` in as the entire user message and nothing else, so
 * every detail the prompt needs has to be inside it and in the shape the prompt
 * was told to expect. Those shapes are the two prompt tickets' contracts —
 * PLAT-11178 for a Section, PLAT-11058 for a Chapter — and this module is the
 * only writer of them, for both naming callers.
 *
 * What is deliberately absent, in both: timings, durations, the passage's own
 * position, the internal `spk_X` labels, resolved speaker names, and (for a
 * Section) its current name — that name is the clip's title, the thing a creator
 * asking for a generated name is overriding (PLAT-11178 DS1).
 *
 * Pure — no React, no store access, no MobX.
 */

/**
 * The DatoCMS `ai_prompt` records these strings are written for, resolved by
 * title at generation time. Authored in PLAT-11178 and PLAT-11058 respectively —
 * a title changed there has to be changed here, which is why they sit beside the
 * functions that build their input rather than in each caller.
 */
export const SECTION_NAME_PROMPT_TITLE = 'Reel Util | Name a Section';
export const CHAPTER_NAME_PROMPT_TITLE = 'Reel Util | Name a Chapter';
/**
 * PLAT-11212's record. It sits here with the other two even though this module
 * composes nothing for it: a reel is named from `chapterizationBaseText`, the
 * same description of the whole reel the chaptering prompt reads (PLAT-11213
 * DS6), so there is no `reelNameBaseText` to keep the title beside. What holds
 * the three together is that they are the titles a naming caller resolves.
 */
export const REEL_NAME_PROMPT_TITLE = 'Reel Util | Name a Reel';

/**
 * Budget for one Section's own naming input. Generous: a Section is a short
 * passage, and the cap is a bound rather than a shape the prompt relies on.
 */
export const SECTION_BASE_TEXT_MAX_CHARS = 1600;

/**
 * Budget per Section *inside* a Chapter. A Chapter can hold several minutes of
 * transcript, so each Section contributes its opening and closing lines rather
 * than its middle — which is what keeps the last Section represented instead of
 * the first one crowding the others out (PLAT-11064).
 */
export const CHAPTER_SECTION_MAX_CHARS = 600;

/** Sentence end followed by whitespace — the fallback cut when a turn is long. */
const SENTENCE_BOUNDARY = /(?<=[.!?])\s+/;

/**
 * One Section's words as speaker turns, and nothing else (PLAT-11178's input
 * contract).
 *
 * Empty when every word in the Section has been removed or cut — the caller
 * declines to generate rather than posting an empty prompt.
 */
export function sectionBaseText(
  timeline: Timeline,
  section: Pick<ReelSection, 'elementIds'>,
): string {
  const text = joinSpeakerTurns(sectionTokens(timeline, section.elementIds));
  return capPassage(text, SECTION_BASE_TEXT_MAX_CHARS);
}

/**
 * Every Section in the Chapter, in timeline order, each headed by a delimiter
 * and — where one is stored — its name (PLAT-11058's input contract):
 *
 *     Section 1 — Cold open
 *     Speaker 1: …
 *
 *     Section 2
 *     Speaker 2: …
 *
 * Only *stored* names reach the heading. `openingWords` is a display fallback
 * for an unnamed Section, and six words of speech under a name heading would
 * teach the model that a sentence fragment is what a title looks like here
 * (PLAT-11058 DS9).
 *
 * The headings number the Sections that are actually sent, contiguously: the
 * number is a delimiter for the model, not the Section's position in the reel,
 * which is deliberately withheld (PLAT-11058 DS7).
 */
export function chapterBaseText(
  timeline: Timeline,
  sections: ReadonlyArray<Pick<ReelSection, 'id' | 'elementIds'>>,
  names?: Record<string, ReelSectionName | undefined>,
): string {
  const blocks: string[] = [];

  for (const section of sections) {
    const said = capPassage(
      joinSpeakerTurns(sectionTokens(timeline, section.elementIds)),
      CHAPTER_SECTION_MAX_CHARS,
    );
    // A Section with nothing left to read is left out rather than sent as a
    // bare heading — a heading with no words under it says only that something
    // is missing.
    if (!said) continue;

    const storedName = names?.[section.id]?.name?.trim();
    const heading = storedName
      ? `Section ${blocks.length + 1} — ${storedName}`
      : `Section ${blocks.length + 1}`;
    blocks.push(`${heading}\n${said}`);
  }

  return blocks.join('\n\n');
}

const isActive = (token: { state?: string }) =>
  token.state !== 'removed' && token.state !== 'cut';

/**
 * The Section's surviving tokens, punctuation included, in reading order.
 *
 * `getWordsWithPunctuation` rather than `getWords`: sentence boundaries have to
 * survive, both for the model and for {@link capPassage}. Removed and cut words
 * are dropped — they are grey strikethroughs in the transcript and absent from
 * the render, so the model must not read them.
 */
function sectionTokens(timeline: Timeline, elementIds: readonly string[]) {
  const track = timeline.tracks.find(
    (t): t is TranscriptTrack => t instanceof TranscriptTrack,
  );
  if (!track) return [];

  const elementsById = new Map<string, VideoTranscriptElement>(
    track.elements.map(el => [el.id, el]),
  );

  return elementIds.flatMap(id => {
    const element = elementsById.get(id);
    if (!element) return [];
    return element
      .getClipTranscriptSegment()
      .getWordsWithPunctuation()
      .filter(isActive);
  });
}

/**
 * Bound a passage to `maxChars` by keeping its opening and closing and dropping
 * the middle — the shape both prompts are told to expect for long input.
 *
 * Cuts at the coarsest boundary that works: whole speaker turns first, then
 * whole sentences within a single turn, then whole words. Deterministic, so the
 * same passage always sends the same string and a prompt tuned on it stays
 * tuned. No elision marker: the prompts state that long input arrives shortened,
 * and a stray `…` is one more thing for a model to copy into a label.
 */
function capPassage(text: string, maxChars: number): string {
  if (text.length <= maxChars) return text;

  const lines = text.split('\n');
  if (lines.length > 1) return keepEnds(lines, maxChars, '\n');

  const sentences = text.split(SENTENCE_BOUNDARY);
  if (sentences.length > 1) return keepEnds(sentences, maxChars, ' ');

  return keepEnds(text.split(' '), maxChars, ' ');
}

/**
 * Take parts from the front until half the budget is spent, then from the back
 * until the whole of it is, preserving order. `spent` is the joined length
 * exactly — separators counted as they are actually added — so the result is
 * within `maxChars` rather than approximately so.
 *
 * The one overrun: a single part longer than the budget is still kept, because
 * a passage that returns nothing cannot be named.
 */
function keepEnds(
  parts: readonly string[],
  maxChars: number,
  separator: string,
): string {
  const half = Math.floor(maxChars / 2);

  const opening: string[] = [];
  let spent = 0;
  for (const part of parts) {
    const cost = part.length + (opening.length ? separator.length : 0);
    if (opening.length && spent + cost > half) break;
    opening.push(part);
    spent += cost;
  }

  const closing: string[] = [];
  for (let i = parts.length - 1; i >= opening.length; i -= 1) {
    const cost = parts[i].length + separator.length;
    if (spent + cost > maxChars) break;
    closing.unshift(parts[i]);
    spent += cost;
  }

  return [...opening, ...closing].join(separator);
}
